import React, { useCallback, useEffect, useState } from 'react';
import { Card, Typography, Form, Input, Button, Space, message, Spin, Switch } from 'antd';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const { Title } = Typography;

const SETTINGS_KEYS = {
  generalNotice: 'ServiceSmsGeneralNotification',
  accepted: 'ServiceSmsAccepted',
  acceptedEnabled: 'ServiceSmsAcceptedEnabled',
  inRepair: 'ServiceSmsInRepair',
  inRepairEnabled: 'ServiceSmsInRepairEnabled',
  completed: 'ServiceSmsCompleted',
  completedEnabled: 'ServiceSmsCompletedEnabled',
  delivered: 'ServiceSmsDelivered',
  deliveredEnabled: 'ServiceSmsDeliveredEnabled',
  appointmentSmsMessage: 'AppointmentSmsMessage',
  appointmentSmsEnabled: 'AppointmentSmsEnabled',
};

const GeneralSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { menuStyle, setMenuStyle } = useTheme();

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Record<string, string>>('/generalsettings');
      const parseBool = (v: string | undefined) => v === 'true' || v === '1';
      form.setFieldsValue({
        generalNotice: data?.[SETTINGS_KEYS.generalNotice] ?? '',
        accepted: data?.[SETTINGS_KEYS.accepted] ?? '',
        acceptedEnabled: parseBool(data?.[SETTINGS_KEYS.acceptedEnabled]),
        inRepair: data?.[SETTINGS_KEYS.inRepair] ?? '',
        inRepairEnabled: parseBool(data?.[SETTINGS_KEYS.inRepairEnabled]),
        completed: data?.[SETTINGS_KEYS.completed] ?? '',
        completedEnabled: parseBool(data?.[SETTINGS_KEYS.completedEnabled]),
        delivered: data?.[SETTINGS_KEYS.delivered] ?? '',
        deliveredEnabled: parseBool(data?.[SETTINGS_KEYS.deliveredEnabled]),
        appointmentSmsMessage: data?.[SETTINGS_KEYS.appointmentSmsMessage] ?? 'Sayın {adSoyad}, {date} tarihinde {plaka} plakalı aracınıza ilişkin randevunuz oluşturulmuştur.',
        appointmentSmsEnabled: parseBool(data?.[SETTINGS_KEYS.appointmentSmsEnabled]),
      });
    } catch (error) {
      message.error('Genel ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await api.post('/generalsettings', {
        [SETTINGS_KEYS.generalNotice]: values.generalNotice ?? '',
        [SETTINGS_KEYS.accepted]: values.accepted ?? '',
        [SETTINGS_KEYS.acceptedEnabled]: values.acceptedEnabled ? 'true' : 'false',
        [SETTINGS_KEYS.inRepair]: values.inRepair ?? '',
        [SETTINGS_KEYS.inRepairEnabled]: values.inRepairEnabled ? 'true' : 'false',
        [SETTINGS_KEYS.completed]: values.completed ?? '',
        [SETTINGS_KEYS.completedEnabled]: values.completedEnabled ? 'true' : 'false',
        [SETTINGS_KEYS.delivered]: values.delivered ?? '',
        [SETTINGS_KEYS.deliveredEnabled]: values.deliveredEnabled ? 'true' : 'false',
        [SETTINGS_KEYS.appointmentSmsMessage]: values.appointmentSmsMessage ?? '',
        [SETTINGS_KEYS.appointmentSmsEnabled]: values.appointmentSmsEnabled ? 'true' : 'false',
      });
      message.success('Genel ayarlar kaydedildi');
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={3}>Genel Ayarlar</Title>
        <Card title="Menü Görünümü" bordered={false}>
          <Space align="center">
            <Switch
              checked={menuStyle === 'colored'}
              onChange={(checked) => setMenuStyle(checked ? 'colored' : 'bw')}
              checkedChildren="Renkli menü"
              unCheckedChildren="Siyah beyaz menü"
            />
            <Typography.Text type="secondary">
              {menuStyle === 'colored' ? 'Menü öğeleri gruplara göre renkli gösteriliyor.' : 'Menü tek renk (siyah/beyaz) gösteriliyor.'}
            </Typography.Text>
          </Space>
        </Card>
        <Card title="Servis SMS Metinleri" bordered={false}>
          <Form form={form} layout="vertical">
            <Form.Item
              name="generalNotice"
              label="Genel Servis Bildirimi (Değişkenler: {adSoyad}, {plaka}, {fişNo})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {plaka} plakalı aracınızın periyodik bakımı yaklaşıyor."
              />
            </Form.Item>
            <Form.Item label="Kabul" name="acceptedEnabled" valuePropName="checked">
              <Switch checkedChildren="SMS Açık" unCheckedChildren="SMS Kapalı" />
            </Form.Item>
            <Form.Item
              name="accepted"
              label="Kabul SMS (Değişkenler: {adSoyad}, {plaka}, {fişNo}, {durum})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {plaka} plakalı aracınız kabul edilmiştir."
              />
            </Form.Item>
            <Form.Item label="Onarımda" name="inRepairEnabled" valuePropName="checked">
              <Switch checkedChildren="SMS Açık" unCheckedChildren="SMS Kapalı" />
            </Form.Item>
            <Form.Item
              name="inRepair"
              label="Onarımda SMS (Değişkenler: {adSoyad}, {plaka}, {fişNo}, {durum})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {plaka} plakalı aracınız onarıma alınmıştır."
              />
            </Form.Item>
            <Form.Item label="Tamamlandı" name="completedEnabled" valuePropName="checked">
              <Switch checkedChildren="SMS Açık" unCheckedChildren="SMS Kapalı" />
            </Form.Item>
            <Form.Item
              name="completed"
              label="Tamamlandı SMS (Değişkenler: {adSoyad}, {plaka}, {fişNo}, {durum})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {plaka} plakalı aracınızın bakımı tamamlanmıştır."
              />
            </Form.Item>
            <Form.Item label="Teslim Edildi" name="deliveredEnabled" valuePropName="checked">
              <Switch checkedChildren="SMS Açık" unCheckedChildren="SMS Kapalı" />
            </Form.Item>
            <Form.Item
              name="delivered"
              label="Teslim Edildi SMS (Değişkenler: {adSoyad}, {plaka}, {fişNo}, {durum})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {plaka} plakalı aracınız teslim edilmiştir."
              />
            </Form.Item>
          </Form>
        </Card>
        <Card title="Randevu SMS" bordered={false}>
          <Form form={form} layout="vertical">
            <Form.Item label="Randevu Oluşturulduğunda SMS" name="appointmentSmsEnabled" valuePropName="checked">
              <Switch checkedChildren="SMS Açık" unCheckedChildren="SMS Kapalı" />
            </Form.Item>
            <Form.Item
              name="appointmentSmsMessage"
              label="SMS metni (Değişkenler: {adSoyad}, {date}, {plaka}, {vehicleInfo})"
            >
              <Input.TextArea
                rows={3}
                placeholder="Sayın {adSoyad}, {date} tarihinde {plaka} plakalı aracınıza ilişkin randevunuz oluşturulmuştur."
              />
            </Form.Item>
          </Form>
        </Card>
        <Space>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Kaydet
          </Button>
        </Space>
      </Space>
    </Spin>
  );
};

export default GeneralSettings;
